import React, { useEffect, useCallback } from 'react';
import {
  Dialog,
  Box,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  MobileStepper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { useOnboarding } from '../../context/OnboardingContext';
import { OnboardingStep, ONBOARDING_STEPS } from './OnboardingStep';

export const OnboardingModal: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    showOnboarding,
    currentStep,
    totalSteps,
    nextStep,
    prevStep,
    skipOnboarding,
    completeOnboarding,
  } = useOnboarding();

  const isLastStep = currentStep === totalSteps - 1;
  const isFirstStep = currentStep === 0;

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!showOnboarding) return;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          if (!isLastStep) nextStep();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          if (!isFirstStep) prevStep();
          break;
        case 'Escape':
          skipOnboarding();
          break;
        case 'Enter':
          if (isLastStep) completeOnboarding();
          else nextStep();
          break;
      }
    },
    [
      showOnboarding,
      isLastStep,
      isFirstStep,
      nextStep,
      prevStep,
      skipOnboarding,
      completeOnboarding,
    ]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Handle swipe gestures for mobile
  const [touchStart, setTouchStart] = React.useState<number | null>(null);
  const [touchEnd, setTouchEnd] = React.useState<number | null>(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && !isLastStep) {
      nextStep();
    } else if (isRightSwipe && !isFirstStep) {
      prevStep();
    }
  };

  if (!showOnboarding) return null;

  return (
    <Dialog
      open={showOnboarding}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? '100vh' : '80vh',
          bgcolor: '#ffffff',
        },
      }}
    >
      {/* Close/Skip button */}
      <Box
        sx={{
          position: 'absolute',
          top: isMobile ? 'env(safe-area-inset-top, 8px)' : 8,
          right: 8,
          zIndex: 1,
        }}
      >
        <IconButton
          onClick={skipOnboarding}
          aria-label="Skip tutorial"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content area with swipe support */}
      <Box
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          pt: isMobile ? 6 : 4,
          pb: 2,
          overflow: 'hidden',
        }}
      >
        {/* Step content */}
        <OnboardingStep stepIndex={currentStep} />
      </Box>

      {/* Navigation footer */}
      <Box
        sx={{
          pb: isMobile ? 'calc(env(safe-area-inset-bottom, 16px) + 16px)' : 3,
          px: 2,
        }}
      >
        {/* Progress stepper */}
        <MobileStepper
          variant="dots"
          steps={ONBOARDING_STEPS.length}
          position="static"
          activeStep={currentStep}
          sx={{
            bgcolor: 'transparent',
            justifyContent: 'center',
            mb: 2,
            '& .MuiMobileStepper-dot': {
              width: 10,
              height: 10,
              mx: 0.5,
            },
            '& .MuiMobileStepper-dotActive': {
              bgcolor: '#662C8B',
            },
          }}
          backButton={<Box />}
          nextButton={<Box />}
        />

        {/* Navigation buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
          }}
        >
          {/* Back button - only show if not first step */}
          {!isFirstStep && (
            <Button
              onClick={prevStep}
              variant="outlined"
              startIcon={<KeyboardArrowLeft />}
              sx={{
                minWidth: 100,
                borderColor: '#662C8B',
                color: '#662C8B',
                '&:hover': {
                  borderColor: '#662C8B',
                  bgcolor: 'rgba(102, 44, 139, 0.04)',
                },
              }}
            >
              Back
            </Button>
          )}

          {/* Next/Done button */}
          <Button
            onClick={isLastStep ? completeOnboarding : nextStep}
            variant="contained"
            endIcon={!isLastStep ? <KeyboardArrowRight /> : undefined}
            sx={{
              minWidth: 120,
              bgcolor: '#662C8B',
              '&:hover': {
                bgcolor: '#551a7a',
              },
            }}
          >
            {isLastStep ? "Let's Go!" : 'Next'}
          </Button>
        </Box>

        {/* Skip link for first-time users */}
        {isFirstStep && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button
              onClick={skipOnboarding}
              variant="text"
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              Skip tutorial
            </Button>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default OnboardingModal;
